import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import ScrollToTopButton from './components/ScrollToTopButton';
import Home from './pages/NewHome';
import Solutions from './pages/Solutions';
import Technology from './pages/Technology';
import Contact from './pages/Contact';
import Careers from './pages/Careers';
import CareerDashboard from './pages/admin/CareerDashboard';
import Products from './pages/Products';
import Projects from './pages/Projects';
import Gallery from './pages/Gallery';
import About from './pages/About';
import WasteWaterTreatment from './pages/products/WasteWaterTreatment';
import EquipmentSystems from './pages/products/EquipmentSystems';
import Accessories from './pages/products/Accessories';
import SpecialtyChemicals from './pages/products/SpecialtyChemicals';
import BlogEvents from './pages/BlogEvents';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import NotFound from './pages/NotFound';

// Solutions Pages
import ScadaDemo from './pages/system/ScadaDemo';
import IoTDemo from './pages/system/IoTDemo';
import CloudDemo from './pages/system/CloudDemo';
import Equalization from './pages/system/Equalization';
import RoTreatment from './pages/system/RoTreatment';
import DomesticRO from './pages/solutions/DomesticRO';
import IndustrialETP from './pages/solutions/IndustrialETP';
import IndustriesWeServe from './pages/solutions/IndustriesWeServe';
import WaterTreatmentHMI from './pages/system/WaterTreatmentHMI';
import WaterTreatmentLogin from './pages/system/WaterTreatmentLogin';

// Demo Pages
import AIFeatures from './pages/demos/AIFeatures';
// Commented out to fix no-unused-vars warnings
// import WaterTreatment from './pages/demos/WaterTreatment';
// import ProcessControl from './pages/demos/ProcessControl';
import NeuralNetworks from './pages/demos/NeuralNetworks';
import SmartMonitoring from './pages/demos/SmartMonitoring';
import PredictiveAnalytics from './pages/demos/PredictiveAnalytics';

// Treatment Pages
import TreatmentProcessControl from './pages/treatment/ProcessControl';
import WaterTreatmentPage from './pages/demos/WaterTreatment';
import ControlSystemsPage from './pages/demos/ProcessControl';
import PatentTechnologies from './pages/technologies/patent-technologies';
import { CareerProvider } from './context/CareerContext';
import CookieConsent from './components/CookieConsent';
import { LanguageProvider } from './context/LanguageContext';
import './context/LanguageContext'; // Import i18n configuration
import AccessibilityWidget from './components/AccessibilityWidget';
import Sidebar from './components/Sidebar';
import { LoadingProvider } from './context/LoadingContext';
import PageTransition from './components/PageTransition';

const theme = createTheme({
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  palette: {
    primary: {
      main: '#00C6CF',
      light: '#4FACFE',
      dark: '#0099A1',
    },
    text: {
      primary: '#1a1a1a',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
  },
});

function App() {
  // No need for additional useEffect here as we're handling loading in the LoadingContext
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LanguageProvider>
        <CareerProvider>
          <Router>
            <ScrollToTop />
            <PageTransition />
            <Box sx={{ 
              minHeight: '100vh',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Navbar />
              <Box component="main" sx={{ flex: 1 }}>
                <Routes>
                  {/* Main Routes */}
                  <Route path="/" element={<Home />} />
                  
                  {/* About Route */}
                  <Route path="/about" element={<About />} />
                  
                  {/* Career Routes */}
                  <Route path="/careers" element={<Careers />} />
                  <Route path="/admin/careers" element={<CareerDashboard />} />
                  
                  {/* Technology Routes */}
                  <Route path="/technologies" element={<Technology />} />
                  <Route path="/technologies/patent-technologies" element={<PatentTechnologies />} />
                  <Route path="/technologies/ai" element={<AIFeatures />} />
                  <Route path="/technologies/neural-networks" element={<NeuralNetworks />} />
                  <Route path="/technologies/smart-monitoring" element={<SmartMonitoring />} />
                  <Route path="/technologies/predictive-analytics" element={<PredictiveAnalytics />} />

                  {/* Solutions Routes */}
                  <Route path="/solutions" element={<Solutions />} />
                  <Route path="/solutions/scada" element={<ScadaDemo />} />
                  <Route path="/solutions/iot" element={<IoTDemo />} />
                  <Route path="/solutions/cloud" element={<CloudDemo />} />
                  <Route path="/solutions/equalization" element={<Equalization />} />
                  <Route path="/solutions/ro-treatment" element={<RoTreatment />} />
                  <Route path="/solutions/domestic-ro" element={<DomesticRO />} />
                  <Route path="/solutions/industrial-etp" element={<IndustrialETP />} />
                  <Route path="/solutions/industries-we-serve" element={<IndustriesWeServe />} />
                  <Route path="/equalization" element={<Equalization />} />
                  <Route path="/water-treatment-login" element={<WaterTreatmentLogin />} />
                  <Route path="/water-treatment-hmi" element={<WaterTreatmentHMI />} />

                  {/* Treatment Routes */}
                  <Route path="/treatment/water" element={<WaterTreatmentPage />} />
                  <Route path="/treatment/control-systems" element={<ControlSystemsPage />} />
                  <Route path="/treatment/process-control" element={<TreatmentProcessControl />} />

                  {/* Other Routes */}
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/projects" element={<Projects />} />
                  
                  {/* Product Routes */}
                  <Route path="/products" element={<Products />} />
                  <Route path="/products/waste-water-treatment" element={<WasteWaterTreatment />} />
                  <Route path="/products/equipment-systems" element={<EquipmentSystems />} />
                  <Route path="/products/accessories" element={<Accessories />} />
                  <Route path="/products/specialty-chemicals" element={<SpecialtyChemicals />} />
                  
                  {/* Blog & Events Route */}
                  <Route path="/blog-events" element={<BlogEvents />} />
                  
                  <Route path="/gallery" element={<Gallery />} />

                  {/* Legal Routes */}
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  
                  {/* 404 Page - This must be the last route */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Box>
              <Footer />
            </Box>
            <ScrollToTopButton />

          </Router>
        </CareerProvider>
      </LanguageProvider>
      <CookieConsent />
      <AccessibilityWidget />
      <Sidebar />
    </ThemeProvider>
  );
}

export default App;
